import React from 'react';
import { Confirmation, Loader } from 'freemium-ui';
import { get } from 'lodash';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
  useHistory,
} from 'react-router-dom';
import { limits } from 'shared/constants';
import { appLogger, beamerInit, getI18nLangCode, heapInit } from 'shared/utils';
import { useAccessControl } from 'utils/access-control';
import { API_ROOT } from 'utils/config';
import {
  AccountInfoType,
  BreadcrumpDataType,
  ConfirmationType,
  GoToRouteType,
  SegmentResponseType,
  UserType,
} from '../shared/types';
import Header from '../shared/components/Header';
import Icon from '../shared/components/Icon';
import Sidebar from '../shared/components/Sidebar';
import { useExecuteSegment, useGetAccountInfo } from '../shared/hooks';
// import { ReactQueryDevtools } from 'react-query/devtools';
// import Template from 'surveyserv/SurveyTemplate';
import NotFoundPage from '../shared/screens/NotFoundPage';
import { Center } from '../shared/styles';
import Analytics from './screens/analytics';
import Contacts from './screens/contacts';
import { getDefaultCondition } from './screens/contacts/screens';
import Dashboard from './screens/dashboard';
import EmailVerify from './screens/email-verify';
import Freshid from './screens/freshid';
import AccountInvite from './screens/freshid/accountInvite';
import Lists from './screens/lists';
import ProfileSettings from './screens/profile';
import Response from './screens/response';
import Segments from './screens/segments';
import Settings from './screens/settings';
import {
  useGetCurrentAccountUser,
  useGetCurrentUser,
  useGetTenantInfo,
} from './screens/settings/hooks';
import Survey from './screens/survey';
import SurveyTemplate from './screens/survey/template';
import Unsubscribe from './screens/unsubscribe';
import Workflow from './screens/wflow';
import VerificationBanner from './components/VerificationBanner';
import { getSegmentPayloadFromFilters } from './utils';
import { ConfirmationBody, ConfirmationText, DisplayBox } from './styles';
// import Workflow from './screens/workflow';
import i18n from '../i18n';

type RoutesProps = {
  breadcrumpData?: BreadcrumpDataType;
  setBreadcrumpData: (
    str: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
};

const Routes = () => {
  const authToken = window.localStorage.getItem('authToken');

  const [breadcrumpData, setBreadcrumpData] =
    React.useState<BreadcrumpDataType>({
      path: [],
    });

  React.useEffect(() => {
    appLogger('Routes', {
      BUILD_NUMBER: process.env.BUILD_NUMBER,
      COMMIT_ID: process.env.COMMIT_ID,
      APP_ENV: process.env.APP_ENV,
    });
  }, []);

  return (
    <Router basename="/app">
      <Switch>
        <Route path="/freshid">
          <Freshid />
        </Route>
        <Route path="/invite-accept">
          <AccountInvite />
        </Route>
        <Route path="/verify-email">
          <EmailVerify />
        </Route>
        <Route path="/unsubscribe">
          <Unsubscribe />
        </Route>
        <Route path="/">
          {authToken ? (
            <ProtectedRoutes
              breadcrumpData={breadcrumpData}
              setBreadcrumpData={(
                data:
                  | BreadcrumpDataType
                  | React.SetStateAction<BreadcrumpDataType>,
              ) => setBreadcrumpData(data)}
            />
          ) : (
            <Redirect to="/freshid/login" />
          )}
        </Route>
      </Switch>
    </Router>
  );
};

const ProtectedRoutes = ({
  breadcrumpData,
  setBreadcrumpData,
}: RoutesProps) => {
  const history = useHistory();
  const {
    access = [],
    updateRole,
    role,
    updateAccount,
    updateUser,
  } = useAccessControl();
  const [contactsCount, setContactsCount] = React.useState<number>(0);
  const [actionTime, setActionTime] = React.useState<string>('');
  const [confirmationData, setConfirmationData] =
    React.useState<ConfirmationType>({});
  const [currentUser, setCurrentUser] = React.useState<UserType>();

  const { data: tenantInfo } = useGetTenantInfo();

  const { data: accountInfo, isLoading: isLoadingAccountInfo } =
    useGetAccountInfo({
      callback: (isSuccess: boolean, response: AccountInfoType) => {
        if (isSuccess) {
          updateAccount(response);
          const locale = getI18nLangCode(response.locale);
          if (locale !== i18n.language) {
            i18n.changeLanguage(locale);
          }
        }
      },
    });

  // checking if the domain is same as from backend & not changed from the current URL
  const custom_domain =
    accountInfo?.custom_domain_status === 'ACTIVATED' &&
    accountInfo?.custom_domain
      ? accountInfo.custom_domain
      : null;
  const accountUrl: string =
    'https://' + (custom_domain ? custom_domain : accountInfo?.domain);
  if (
    !isLoadingAccountInfo &&
    !location.href.includes(accountUrl?.toLowerCase()) &&
    !window.location.href.includes('localhost') &&
    accountInfo?.domain != undefined
  ) {
    location.href = accountUrl;
  }

  const { mutate: executeDefaultSegment } = useExecuteSegment({});

  const fetchContactCount = () => {
    if (!accountInfo?.mcr_account_id) return;
    const ruleToExecute = getSegmentPayloadFromFilters(
      {},
      getDefaultCondition(accountInfo?.mcr_account_id || ''),
      '',
    );
    executeDefaultSegment({
      rule: ruleToExecute,
      params: {
        page: 1,
        per_page: 1,
      },
      callback: (isSuccess, response: SegmentResponseType) => {
        if (isSuccess) {
          const count = get(response, ['meta', 'totalNumberOfRecords'], 0);
          setContactsCount(count);
          if (window && window.heap) {
            window.heap.addUserProperties({
              contacts_count: count,
            });
          }
        }
      },
    });
  };

  React.useEffect(() => {
    fetchContactCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo?.mcr_account_id]);

  const { data: userInfo, isLoading: isLoadingCurrentUser } = useGetCurrentUser(
    {
      callback: (isSuccess: boolean, response: UserType) => {
        if (isSuccess && response.display_id) {
          setCurrentUser(response);
          updateUser(response);
        }
      },
    },
  );
  const { data: currentAccountUser, isLoading: isLoadingCurrentAccountUser } =
    useGetCurrentAccountUser(currentUser?.display_id || '', role === 'ADMIN');
  React.useEffect(() => {
    if (currentAccountUser?.user_role)
      updateRole(currentAccountUser?.user_role);
  }, [currentAccountUser]);

  const goToRoute: GoToRouteType = ({ path }) => {
    history.push(path);
  };

  React.useEffect(() => {
    if (accountInfo?.status === 'DEACTIVATED') {
      window.location.href = `${window.location.origin}/landing/access-denied.html`;
    }
  }, [accountInfo]);

  React.useEffect(() => {
    if (window.SurveyWidget) {
      if (process.env.APP_ENV === 'production') {
        const contact = {
          first_name: currentUser?.first_name,
          last_name: currentUser?.last_name,
          work_email: currentUser?.email,
          designation: '',
          address: '',
          city: '',
          state: '',
          country: '',
          zipcode: '',
          language: 'EN',
          timezone: accountInfo?.timezone,
          industry: '',
          company_name: accountInfo?.name,
          company_website: '',
          last_emailed: '',
          unsubscribe_time: '',
          email_state: '',
          product_role: currentAccountUser?.user_role,
          product_name: 'FRESHSURVEY',
          product_signup_date: accountInfo?.created_at
            ? new Date(accountInfo?.created_at).toISOString()
            : '',
          account_name: accountInfo?.name,
          account_url: accountInfo?.domain,
          product_account_id: accountInfo?.id,
          plan: 'FREE',
        };

        window.SurveyWidget.init({
          type: 'default',
          delay: 2,
          include_unsubscribe: true,
          survey_id: 'c1955f78-2403-4cec-8dcd-0dd585a70605',
          survey_collector_id: 'SC-1419',
          contact: contact,
        });
      } else if (
        localStorage.getItem('surveyId') !== '' &&
        localStorage.getItem('collectorId') !== ''
      ) {
        window.SurveyWidget.init({
          type: localStorage.getItem('widgetType') || 'default',
          delay: localStorage.getItem('delay') || 5,
          include_unsubscribe:
            localStorage.getItem('unsubscribe') === 'true' || true,
          survey_id: localStorage.getItem('surveyId'),
          survey_collector_id: localStorage.getItem('collectorId'),
          contact: {
            work_email: currentUser?.email,
          },
        });
      }
    }
    if (currentUser) {
      heapInit(currentUser, currentAccountUser, accountInfo);
      beamerInit(currentUser);
    }
  }, [accountInfo, currentAccountUser, currentUser]);

  appLogger('ProtectedRoutes', {
    userInfo,
    tenantInfo,
    accountUrl,
    currentUser,
    accountInfo,
    custom_domain,
    contactsCount,
    currentAccountUser,
  });

  if (
    isLoadingAccountInfo ||
    isLoadingCurrentUser ||
    isLoadingCurrentAccountUser ||
    currentAccountUser?.user_role !== role
  )
    return (
      <Center>
        <Loader />
      </Center>
    );

  return (
    <>
      {/* <ReactQueryDevtools /> */}
      <Confirmation
        width="540px"
        isOpen={Boolean(confirmationData.id)}
        onDismiss={(type) => {
          if (type === 'overlay') return;
          setConfirmationData({});
        }}
        title={confirmationData.title}
        cancelButtonDisabled={confirmationData.cancelButtonDisabled}
        confirmButtonDisabled={confirmationData.confirmButtonDisabled}
        cancelButtonText={
          confirmationData.cancelButtonText || `${i18n.t('cancel')}`
        }
        confirmButtonText={
          confirmationData.confirmButtonText || `${i18n.t('ok')}`
        }
        onConfirm={confirmationData.onConfirm}
      >
        <ConfirmationText>{confirmationData.description}</ConfirmationText>
        {confirmationData.body && (
          <ConfirmationBody>{confirmationData.body()}</ConfirmationBody>
        )}
        {confirmationData.glyph && confirmationData.infoText && (
          <DisplayBox className={confirmationData.className}>
            <div>
              <Icon
                fill={confirmationData.fill}
                width="16"
                height="16"
                glyph={confirmationData.glyph}
              />
            </div>
            <div className="divider" />
            <div className="text-content">{confirmationData.infoText}</div>
          </DisplayBox>
        )}
      </Confirmation>
      <Sidebar />
      <section className="app-section">
        <VerificationBanner />
        <Header
          setActionTime={(val: string) => setActionTime(val)}
          breadcrumpData={breadcrumpData}
          setConfirmationData={setConfirmationData}
          setBreadcrumpData={(str) => setBreadcrumpData(str)}
          accountInfo={accountInfo}
          currentAccountUser={currentAccountUser}
          contactsCount={contactsCount}
          fetchContactCount={fetchContactCount}
        />
        <div className="App">
          <Switch>
            <Route exact path="/">
              <Redirect to="/dashboard" />
            </Route>
            {access.includes('dashboard:read') && (
              <Route path="/dashboard">
                <Dashboard currentAccountUser={currentAccountUser} />
              </Route>
            )}
            {access.includes('analytics:read') && (
              <Route path="/analytics">
                <Analytics />
              </Route>
            )}
            {access.includes('settings:read') && (
              <Route exact path="/settings/templates/:id/edit">
                <SurveyTemplate
                  setConfirmationData={setConfirmationData}
                  setBreadcrumpData={setBreadcrumpData}
                  goToRoute={({ path }) =>
                    goToRoute({
                      path: `/settings${path}`,
                    })
                  }
                  hostConfig={{
                    limits: {
                      ...(accountInfo?.resource_limit_configuration || {}),
                      templates:
                        accountInfo?.resource_limit_configuration?.templates ||
                        limits.templates,
                      no_of_saved_templates:
                        tenantInfo?.limit_config?.no_of_saved_templates ||
                        limits.templates,
                    },
                    API_HOST: API_ROOT,
                  }}
                />
              </Route>
            )}
            {access.includes('settings:read') && (
              <Route exact path="/settings/templates/create">
                <SurveyTemplate
                  setConfirmationData={setConfirmationData}
                  setBreadcrumpData={setBreadcrumpData}
                  goToRoute={({ path }) =>
                    goToRoute({
                      path: `/settings${path}`,
                    })
                  }
                  hostConfig={{
                    limits: {
                      ...(accountInfo?.resource_limit_configuration || {}),
                      templates:
                        accountInfo?.resource_limit_configuration?.templates ||
                        limits.templates,
                      no_of_saved_templates:
                        tenantInfo?.limit_config?.no_of_saved_templates ||
                        limits.templates,
                    },
                    API_HOST: API_ROOT,
                  }}
                />
              </Route>
            )}
            {access.includes('settings:read') && (
              <Route path="/settings">
                <Settings
                  accountInfo={accountInfo}
                  setConfirmationData={setConfirmationData}
                  breadcrumpData={breadcrumpData}
                  setBreadcrumpData={setBreadcrumpData}
                  currentAccountUser={currentAccountUser}
                />
              </Route>
            )}
            {access.includes('workflow:read') && (
              <Route path="/workflow">
                <Workflow
                  setConfirmationData={setConfirmationData}
                  accountInfo={accountInfo || {}}
                />
              </Route>
            )}
            {access.includes('contact:read') && (
              <Route path="/contacts">
                <Contacts
                  setConfirmationData={setConfirmationData}
                  actionTime={actionTime}
                  setBreadcrumpData={setBreadcrumpData}
                  accountInfo={accountInfo}
                  userInfo={userInfo}
                  contactsCount={contactsCount}
                  fetchContactCount={fetchContactCount}
                />
              </Route>
            )}
            {access.includes('contact:read') && (
              <Route path="/lists">
                <Lists
                  setConfirmationData={setConfirmationData}
                  actionTime={actionTime}
                  setBreadcrumpData={setBreadcrumpData}
                  accountInfo={accountInfo}
                  contactsCount={contactsCount}
                  fetchContactCount={fetchContactCount}
                />
              </Route>
            )}
            {access.includes('contact:read') && (
              <Route path="/segments">
                <Segments
                  setConfirmationData={setConfirmationData}
                  actionTime={actionTime}
                  setBreadcrumpData={setBreadcrumpData}
                  accountInfo={accountInfo}
                  userInfo={userInfo}
                  contactsCount={contactsCount}
                  fetchContactCount={fetchContactCount}
                />
              </Route>
            )}
            {access.includes('survey:read') && (
              <Route path="/survey">
                <Survey
                  setBreadcrumpData={setBreadcrumpData}
                  hostConfig={{
                    navigation: {
                      IMPORT_CONTACTS: '?open=import',
                      ADD_CONTACT_SEGMENT: '?open=segment',
                      ADD_CONTACT_LIST: '?open=list',
                      DKIM_SETTINGS: `${window.location.origin}/app/settings/dkim`,
                      ADD_CONTACT: '?open=contact',
                      TRANSLATION_SETTINGS: `${window.location.origin}/app/settings/translation-settings`,
                    },
                    limits: {
                      active_surveys:
                        accountInfo?.resource_limit_configuration
                          ?.active_surveys || limits.active_surveys,
                      surveys:
                        accountInfo?.resource_limit_configuration?.surveys ||
                        limits.surveys,
                      workflows:
                        accountInfo?.resource_limit_configuration?.workflows ||
                        limits.workflows,
                    },
                    API_HOST: API_ROOT,
                  }}
                />
              </Route>
            )}
            {access.includes('response:read') && (
              <Route path="/response">
                <Response
                  accountInfo={accountInfo}
                  userInfo={userInfo}
                  setConfirmationData={setConfirmationData}
                />
              </Route>
            )}
            <Route path="/profile">
              <ProfileSettings />
            </Route>
            <Route path="*">
              <NotFoundPage />
            </Route>
          </Switch>
        </div>
      </section>
    </>
  );
};

export default Routes;
